'use client'

import { useEffect, useState } from 'react'
import type { LabelSuggestion } from '../media-preview/types'
import type { EnrichedMediaResult } from '@/enrichment'
import { useDocumentView } from '@/document'

interface UseLabelCheckingOptions {
  enrichedData: EnrichedMediaResult | null
}

interface UseLabelCheckingReturn {
  labelSuggestions: LabelSuggestion[]
  isCheckingLabels: boolean
  toggleLabelSuggestion: (externalId: string) => void
}

/**
 * Hook to check for duplicate labels when creating artists.
 * Compares label external IDs from enriched data against existing labels
 * in the Automerge document to suggest linking vs creating new labels.
 */
export function useLabelChecking({
  enrichedData,
}: UseLabelCheckingOptions): UseLabelCheckingReturn {
  const [labelSuggestions, setLabelSuggestions] = useState<LabelSuggestion[]>(
    [],
  )
  const [isCheckingLabels, setIsCheckingLabels] = useState(false)
  const doc = useDocumentView()

  useEffect(() => {
    // Reset if not an artist or if enriched data is null
    if (enrichedData?.type !== 'artist' || !enrichedData?.enriched) {
      setLabelSuggestions([])
      return
    }

    // Check if labelInfo exists and has items
    const labelInfo = enrichedData.enriched.labelInfo
    if (!labelInfo || labelInfo.length === 0) {
      setLabelSuggestions([])
      return
    }

    const checkLabels = () => {
      setIsCheckingLabels(true)
      try {
        // Check which labels already exist in the Automerge document
        const existingMap: Record<string, string> = {}

        if (doc) {
          const labelEntries = Object.values(doc.entries).filter(
            (entry) => entry.type === 'label',
          )
          labelInfo.forEach((label) => {
            const existing = labelEntries.find((entry) =>
              entry.metadata && 'externalId' in entry.metadata
                ? entry.metadata.externalId === label.externalId
                : false,
            )
            if (existing) {
              existingMap[label.externalId] = existing.id
            }
          })
        }

        const suggestions: LabelSuggestion[] = labelInfo.map((label) => ({
          externalId: label.externalId,
          name: label.name,
          existingLabelId: existingMap[label.externalId] ?? null,
          isSelected: !!existingMap[label.externalId],
        }))

        setLabelSuggestions(suggestions)
      } catch (err) {
        console.error('Failed to check labels:', err)
        // Still show labels even if check fails
        const suggestions: LabelSuggestion[] = labelInfo.map((label) => ({
          externalId: label.externalId,
          name: label.name,
          existingLabelId: null,
          isSelected: false,
        }))
        setLabelSuggestions(suggestions)
      } finally {
        setIsCheckingLabels(false)
      }
    }

    checkLabels()
  }, [
    enrichedData?.enriched?.labelInfo,
    enrichedData?.type,
    enrichedData?.enriched,
    doc,
  ])

  const toggleLabelSuggestion = (externalId: string) => {
    setLabelSuggestions((prev) =>
      prev.map((label) =>
        label.externalId === externalId && !label.existingLabelId
          ? { ...label, isSelected: !label.isSelected }
          : label,
      ),
    )
  }

  return {
    labelSuggestions,
    isCheckingLabels,
    toggleLabelSuggestion,
  }
}
